import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Wifi, Phone, Mail, CalendarDays, ArrowRight } from "lucide-react";
import { WishlistCard } from "../components/guest/WishlistCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestPortal() {
  const [params] = useSearchParams();
  const booking = params.get("booking");
  const email = params.get("email");
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!booking || !email || !token) { setErr("Missing token"); return; }
    axios.get(`${API}/guest-portal`, { params: { booking, email, token } })
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid link"));
  }, [booking, email, token]);

  const map = info?.map_query
    ? `https://www.google.com/maps?q=${encodeURIComponent(info.map_query)}&output=embed`
    : null;
  const mapLink = info?.map_query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.map_query)}`
    : null;

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="max-w-md text-center border border-border rounded-lg p-8 bg-card">
        <div className="heading text-2xl mb-2">Link unavailable</div>
        <div className="text-sm text-muted-foreground">{err}</div>
      </div>
    </div>
  );
  if (!info) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold">Roots by Sanjeevani Farms</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-3">Guest portal</div>
        <h1 className="heading text-4xl">You're going to {info.project_name}.</h1>
        <div className="mt-3 text-sm text-muted-foreground">Hosted by {info.host_name} · Plot {info.plot_number}</div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold"><CalendarDays size={12} /> Stay</div>
            <div className="text-sm mt-2 font-medium">{info.check_in} <ArrowRight size={12} className="inline mx-1" /> {info.check_out}</div>
            <div className="text-xs text-muted-foreground mt-1">{info.guests} guest(s)</div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold"><Wifi size={12} /> WiFi</div>
            <div className="text-sm mt-2 font-medium" data-testid="wifi-ssid">{info.wifi_ssid || "Ask on arrival"}</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono" data-testid="wifi-pass">{info.wifi_password || ""}</div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold"><Phone size={12} /> Manager</div>
            {info.manager_phone
              ? <a href={`tel:${info.manager_phone}`} className="text-sm mt-2 block font-medium hover:text-primary" data-testid="manager-phone">{info.manager_phone}</a>
              : <div className="text-sm mt-2 text-muted-foreground">Contact via email</div>}
            {info.manager_email && (
              <a href={`mailto:${info.manager_email}`} className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1 hover:text-primary"><Mail size={11} /> {info.manager_email}</a>
            )}
          </div>
        </div>

        {info.directions && (
          <div className="mt-6 p-5 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2"><MapPin size={12} /> Directions</div>
            <p className="text-sm leading-relaxed" data-testid="directions">{info.directions}</p>
          </div>
        )}

        {info.rsvp_status === "confirmed" && (
          <WishlistCard booking={booking} email={email} token={token} initial={info.wishlist || []} />
        )}

        {map && (
          <div className="mt-6 border border-border rounded-lg overflow-hidden bg-card">
            <iframe title="Map" src={map} className="w-full h-80" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="guest-map" />
            <a href={mapLink} target="_blank" rel="noreferrer" className="block text-[11px] uppercase tracking-widest text-primary font-semibold px-4 py-3 border-t border-border hover:bg-secondary/40">
              Open in Google Maps <ArrowRight size={11} className="inline ml-1" />
            </a>
          </div>
        )}

        <div className="mt-8 p-4 rounded-md bg-primary/5 border border-primary/20 text-xs text-muted-foreground">
          Arrival window: 12:00 – 20:00 IST. Bring warm layers for evenings and comfortable walking shoes. The estate manager will meet you at the gate.
        </div>

        <footer className="mt-16 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          © Roots by Sanjeevani Farms
        </footer>
      </div>
    </div>
  );
}
